import {
	convertLocalToUtcAccordingToTimezone,
	convertUtcToLocalAccordingToTimezone,
	getDiff,
	humanizeDuration,
	toISOString,
} from "@sembark-travel/datetime-utils"
import type { ILocation } from "../Locations"
import { TChildrenArray } from "../Tourists"
import { IFlightServicesParams, IPreviewFlightData } from "./NewCalculatePrice"
import { TWorldAirline } from "../World"

export type TFlightClass =
	| "Business"
	| "Economy"
	| "First Class"
	| "Premium Economy"
export const DEFAULT_FLIGHT_CLASS: TFlightClass = "Economy"
export const ADDITIONAL_FLIGHT_OPTIONS = [
	{
		id: 2,
		name: "Baggage",
		value: "baggage",
	},
	{
		id: 3,
		name: "Fare Type",
		value: "fare",
	},
	{
		id: 4,
		name: "Meals",
		value: "meal",
	},
	{
		id: 5,
		name: "Notes for Customer",
		value: "notes_customer",
	},
]
export const FLIGHT_CATEGORY_CLASSES: Array<TFlightClass> = [
	"Economy",
	"Premium Economy",
	"Business",
	"First Class",
]

export interface IFlightPricing {
	currency: string
	calculated_price?: number
	given_price?: number
	comments?: string
	no_of_adults: number
	children: number
	infants: number
}
export interface IFlightSchedule {
	source: ILocation // from departure in auto airport id of source
	destination: ILocation // to arrival
	departs_at: string
	departs_at_local: string
	arrives_at: string
	arrives_at_local: string
	airline: TWorldAirline
	airline_id: number
	number_plate: string
	number_plate_formatted: string
	arrival_terminal?: number | string
	departure_terminal?: number | string
	remarks?: string
}
export type TFlightInfoType =
	| "adult_check_in_baggage"
	| "adult_hand_baggage"
	| "infant_hand_baggage"
	| "infant_check_in_baggage"
	| "meal_preference"
	| "fare_type"
	| "category_class"

export type TFlightGroupType =
	| "ROUND_TRIP_ONWARD"
	| "ROUND_TRIP_RETURN"
	| "ONE_WAY"
	| "MULTI_CITY"

export type TFlightFormFillMode = "AUTO" | "MANUAL"
export type TFlightType = "ROUND_TRIP" | "ONE_WAY" | "MULTI_CITY"
export interface IFlightService extends IFlightPricing {
	id: number
	groups: Array<{
		type: TFlightGroupType
		schedules: Array<IFlightSchedule>
		info?: Array<{
			id: number
			type: TFlightInfoType
			value: string
		}>
		remarks?: string
	}>
}

export interface IFlightAirPort {
	schedule_date: string
	schedule_date_local: string
	gate: string | null
	terminal?: string | number | null
	airport?: ILocation | null
}

export interface IFlightSchedules {
	schedule: {
		departure: IFlightAirPort
		arrival: IFlightAirPort
		duration: string
		duration_formatted: string
	} | null
	flight: {
		id?: number | string
		number_plate: string
		number_plate_formatted: string
		airline: TWorldAirline
	}
}

export function addLabelToAirport(options: ILocation) {
	const city = options.city?.name || options.iata || ""
	return {
		...options,
		label: `${city ? `${city}: ` : ""} ${options.name}`,
	}
}

export function getChildrenDistribution(children: TChildrenArray): [
	/**
	 * Infants, age within (0,2]
	 */
	number,
	/**
	 * Children, age within (2, 12]
	 */
	number,
	/**
	 * Adults with age greater than 12
	 */
	number,
] {
	const totalCount = (arr: number[]) =>
		arr.reduce<number>((sum, n) => sum + n, 0)
	return [
		totalCount(children.filter((c) => c.age <= 2).map((c) => c.count)),
		totalCount(
			children.filter((c) => c.age > 2 && c.age <= 12).map((c) => c.count)
		),
		totalCount(children.filter((c) => c.age > 12).map((c) => c.count)),
	]
}

export const FLIGHT_TYPE = {
	ONE_WAY: "One Way",
	MULTI_CITY: "Multi City",
	ROUND_TRIP: "Round Trip",
	ROUND_TRIP_ONWARD: "Onward",
	ROUND_TRIP_RETURN: "Return",
}
export function transformFlightsFormDataToPreviewData(
	flightsFormData: IFlightServicesParams["flights"]
): Array<{ groups: Array<IPreviewFlightData>; pricing: IFlightPricing }> {
	if (!flightsFormData?.length) return []

	return flightsFormData.map((flight) => {
		const flightDetails = (flight.groups || []).map((group) => {
			const scheduleDetails: IPreviewFlightData["flight_details"] =
				group.schedules.map((schedule) => {
					const departs_at = schedule.departs_at_local
						? convertLocalToUtcAccordingToTimezone(
								schedule.departs_at_local,
								schedule.source?.timezone?.name
							)
						: schedule.departs_at
							? convertUtcToLocalAccordingToTimezone(schedule.departs_at)
							: ""
					const arrives_at = schedule.arrives_at_local
						? convertLocalToUtcAccordingToTimezone(
								schedule.arrives_at_local,
								schedule.destination?.timezone?.name
							)
						: schedule.arrives_at
							? convertUtcToLocalAccordingToTimezone(schedule.arrives_at)
							: ""
					const duration = humanizeDuration(getDiff(departs_at, arrives_at))
					return {
						departure: {
							schedule_date: departs_at,
							schedule_date_local: schedule.departs_at_local
								? toISOString(schedule.departs_at_local)
								: "",
							gate: null,
							terminal: schedule.departure_terminal,
							airport: schedule.source || null,
						},
						arrival: {
							schedule_date: arrives_at,
							schedule_date_local: schedule.arrives_at_local
								? toISOString(schedule.arrives_at_local)
								: "",
							gate: null,
							terminal: schedule.arrival_terminal,
							airport: schedule.destination || null,
						},
						duration,
						duration_formatted: duration,
						flight: {
							number_plate: schedule.airline?.number_plate_formatted || "",
							airline: schedule.airline?.airline,
						},
					}
				})

			const from =
				scheduleDetails[0]?.departure?.airport?.city?.name ||
				scheduleDetails[0]?.departure?.airport?.iata ||
				scheduleDetails[0]?.departure?.airport?.name ||
				""

			const to =
				scheduleDetails.at(-1)?.arrival?.airport?.city?.name ||
				scheduleDetails.at(-1)?.arrival?.airport?.iata ||
				scheduleDetails.at(-1)?.arrival?.airport?.name ||
				""

			const via = scheduleDetails
				.slice(1)
				.map((f) => f.departure?.airport?.iata)
				.filter(Boolean) // filter out undefined/empty

			const flight_number = [
				...new Set(scheduleDetails.map((f) => f.flight.number_plate)),
			]
			const additionalOptions = (group.additional_options || []).map(
				(field) => field.value
			)
			return {
				flight_details: scheduleDetails,
				from,
				to,
				type_of_flight: group.type
					? FLIGHT_TYPE[group.type]
					: FLIGHT_TYPE["ONE_WAY"],
				info: {
					adult_check_in_baggage: additionalOptions?.includes("baggage")
						? group.info?.adult_check_in_baggage?.value
						: undefined,
					adult_hand_baggage: additionalOptions?.includes("baggage")
						? group.info?.adult_hand_baggage?.value
						: undefined,
					infant_check_in_baggage:
						additionalOptions?.includes("baggage") && flight.pricing.infants
							? group.info?.infant_check_in_baggage?.value
							: undefined,
					infant_hand_baggage:
						additionalOptions?.includes("baggage") && flight.pricing.infants
							? group.info?.infant_hand_baggage?.value
							: undefined,
					meal_preference: additionalOptions?.includes("meal")
						? group.info?.meal_preference?.value
						: undefined,
					fare_type: additionalOptions?.includes("fare")
						? group.info?.fare_type?.value
						: undefined,
					category_class: group.info?.category_class,
				},
				no_flight:
					scheduleDetails.length > 1
						? `${scheduleDetails.length - 1} Stop`
						: `Non Stop`,
				via: [
					...new Set(
						(via || []).filter((item): item is string => item !== undefined)
					),
				],
				flight_number,
				remarks: additionalOptions?.includes("notes_customer")
					? group.remarks
					: undefined,
			}
		})
		return {
			groups: flightDetails,
			pricing: flight.pricing,
		}
	})
}
